import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import './design-guidelines-styles.css';
import { Button } from 'react-magma-dom';
export const _frontmatter = {
  "pageTitle": "Button Design Guidelines",
  "title": "Button",
  "order": 1
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageContent componentName="button" type="design" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Buttons are clickable items used to perform an action.
      </LeadParagraph>
      <h2>{`Usage`}</h2>
      <p>{`Buttons communicate actions that users can take.`}</p>
      <ul>
        <li parentName="ul">{`Submit a form.`}</li>
        <li parentName="ul">{`Begin a new task.`}</li>
        <li parentName="ul">{`Trigger a new UI element to appear on the page.`}</li>
        <li parentName="ul">{`Specify a new or next step in a process.`}</li>
      </ul>
      <hr></hr>
      <h2>{`Types`}</h2>
      <h3>{`Text Button (low emphasis)`}</h3>
      <p>{`Text buttons are typically used for less important actions.`}</p>
      <Button color="primary" variant="link" mdxType="Button">
  Text Button
      </Button>
      <h3>{`Outlined Button (medium emphasis)`}</h3>
      <p>{`Outlined buttons are used for more emphasis than text buttons due to the stroke.`}</p>
      <Button color="secondary" variant="solid" mdxType="Button">
  Outlined Button
      </Button>
      <h3>{`Solid Button (high emphasis)`}</h3>
      <p>{`Solid buttons have more emphasis, as they use use a color fill.`}</p>
      <Button mdxType="Button">Solid Button</Button>
      <hr></hr>
      <h2>{`Anatomy`}</h2>
      <h3>{`Text Label`}</h3>
      <p>{`Almost all buttons use text labels, which describe the action that will occur if a user taps a button.`}</p>
      <h4>{`All Caps or Title Case?`}</h4>
      <p><strong parentName="p">{`By default Magma uses all caps text labels.`}</strong>{` This is to provide a greater contrast between buttons and surrounding text, therefore making buttons easier to find. This is especially helpful for buttons that don't have a visible container.`}</p>
      <p>{`Text labels should always be short and have clear purpose. If a button requires a text label with three or more words, try really hard to shorten it. If you can’t, you may use title case to help reduce the size of the button’s footprint. We recommend buttons using title case also have an outline or solid container, to help differentiate the button from the text around it.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "111.96911196911196%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAWCAIAAABPIytRAAAACXBIWXMAAAsTAAALEwEAmpwYAAAD60lEQVQ4y33QzU8jZRwHcP6GvZi4fZm30hKgvAx11ailQDcubelM552CRDFm15PRk64HV5YCZfewB+Uta4yJJ4+uaILZ+AdoPJgNJzDFznun89YW6MuM6QuCxuw3vzx5Lp/5/p7pY3B8EUu/M88tZbOLncnOzzM0TRAEhmGpFJZMpBOz6UQinUhgyQT26ivR7e1d13Uty+qj06nlxOxHRPo9hl6iaZamyEybzc2lU6n0XArHcZIkaZKkMwSF42Q0Or27+7iH2ZvxD29OfZG+9QlNLDP0AstwLMMy7H/CMAxJUQRBxmLxvb2vevjOVPTbWPgPBv2GSd1hmaV5lmNZ7iIsy9EMR9EcQbJ4hsZwajIW397Z6+Hbb0z+SoPuwY2fqeH3IxEGm8suZDusXU/TLEVzJMUR1CXe2trpYSIQuP8S+PXiy3fD4FuI/1YwkJyKsSyXXcgybLuToudJistcNEcn41tbFz9sDABHPUAG9L0OgEMANAGCN0BgdCg8NTWDZwg8Q6UxgqDa/RhOzWFEdHLmy3+aEQAOA2A8BA36wRAAQQCMgAHIB/m9UH9gIBQaQpBQODwejc3MJtNpjJiejm/vXDRDfgjyg4AXAH0A4AX9XtDrAb0eyOeFPNeB6y8CXg/YvngAAAwMDo74/PDGxmYPB+BgP9wfhPv74X4EDiJwEIZDnTOIwCEEGUDgAQQZgJEBCA7BcOjatRfu3bvfw+g4mhoZ/mAslBodiaAoik6gaGQcjYyhkbHxyMhYJDw6MTwyMRRGB4fHh8Koxwfl1jZ6OLe6+mhl5fHnnz1aWcmttpPL5Vbbs7aaW8vn1x8+zD94sLG5uZ7Pr23k1z++++n3T540m03btvvc50Yrt/4snBf+qp8U2yNIzVbLrdfrtm1Xq9W+YrHI8/zR0ZEsy4IgiKLoOE6j0XCcVqVyjnM/Jsl9cuGnJLmfpPbR17777XfZaZ1ZVgcXCoWTk5PDw0Oe54vFoiAIruu2Wo7ruqdnjbdv/0IvHiy++5ReOMguP30T++HZoe465z38/LVPz1q6UTfMpmU3TbNRqTYdx63VapVKpY1VVTVN07Is0zQ1TTMMo1arlbvRytWqXa+fGoZmGFq1YlqW4bpXsCiKiqLoui7LstqJpmmlUkmSJLkTVVUNwyiXy5Zl6br+L6yqaqFQ0HW9W6aqqizLpmmWSiVFUURRlCTJcZyrb7nEx8fHuq4LglAoFCzLkmWZ5/muFwTBMIxSqcTzfKPRcF23+5VLLAiCaZqSJFmWpWlat627v6IokiQpiiLLsm3b/9MsSVL3kd09u6ckSaIodu+yLEuSZBiGbdutVusq/huvlSyo86GIYAAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/88d8c7323844369a127d9e53c98ddbb4/8ae3e/ex_4_1.png",
                  "srcSet": ["/version/4.9.1/static/88d8c7323844369a127d9e53c98ddbb4/a2ead/ex_4_1.png 259w", "/version/4.9.1/static/88d8c7323844369a127d9e53c98ddbb4/6b9fd/ex_4_1.png 518w", "/version/4.9.1/static/88d8c7323844369a127d9e53c98ddbb4/8ae3e/ex_4_1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`Keep labels short`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "111.96911196911196%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAWCAIAAABPIytRAAAACXBIWXMAAAsTAAALEwEAmpwYAAAEDklEQVQ4y22R208jVRzH+Rv2xcRt6dwKLdAWGBA10VKgJFDKzHQ6txZkwxrX9cnom7sPItANoGV5UG5Zo0aNycaHXdZdN7ubjb55idnEB4wGYknnPu10ZgoJ0HZML4Aavjk5+b18zud7zmliCGISx6YT3FQyOVlbyUSCoWmSJHEcj0bxsQgWGcUiESwSwcci+MsvBdfWNmzbNk2zicailyOj75LYGww9RdMsTcVjVWx8HItGsfEoQRDxeJyOx+kYSRFEPBgc3Ni41YDZ4fA7wwMfYyPv0eRlhp5gGY5lWIb9XxiGiVMUScZDofDm5qcN+OpA8KuQ/3cG/YKJXmWZqQTLsSx3EpblaIajaI6Ms0SMxgmqPxReW99swG++2v8rDdqP+h5Tvrd6exl8PDmRrGFVPU2zFM3FKY6kzuDV1fUGTLrdcy+An02+eM0Pvoa4RlrdYwMhluWSE0mGrTopOhGnuNiJOdgfXl09ebAuAOx0ADGw+RUA7ACgHhDsA4HODv/AwBARI4kYheEkSVX9OEGN42Swf+iTUzMCwH4ADHugdhfoASAIgBHQDTVDLifU4vZ6PB0I4vH7u4OhodExDMPJwcHw2vqJGXJBkAsEnADYDABO0OUEnQ7Q6YCanZDjInDxecDpAKuDAwBAd3t7oNkFLywsNWA33NoCt7TCLS1wCwK3InArDHtqeysCexDEi8BeBPHCiBeCPTDsuXDhuZmZuQaMdqPRgO/tLk+0M9CLoijag6K93WhvF9ob6OrxB3p8gZ4OH9rW0d3W3tXuQx3NUOrGQgNOzc+vzM7e+uD9ldnZ1Hw1qVRqPpWam08tfbhwc2Vp+eZSenkxnV74KL2QTi9eu359a+teuVSyLKvJPi+VSsW27Z3d4rd3+HsPpXsPxK0H4t371VUslsqlI8uy9vf3m7LZLM/zOzs7siwLgiCKYqVSOTw8sm175sbPXvSbKHV/GNvqH7kbGrkDtH350y9SpXJomjU4k8ns7e1tb2/zPJ/NZgVBsG27VCrbtr24/MzXdzsx/YS79JiZekRwD319t397ptr2kVGHz61dLldrf/71X4OR7yZefzp15cdLV35ITD8dIb7/48/CmVlVVcMwTNM0DCOXyxUKhYODg3w+r+t5Tcspqq6qxt8ZMZMRBUGV5Zxt2wcHB8VisQqLoqgoiq7rsiyrteRyOU3TJElSFEXTlHxes6on6/v7lmEU/gOrqprJZHRdz9eiqqosy4ZhaJqmKIooipIk1R//NGfw7u6uruuCIGQyGdM0ZVnmeb7OC4JQKBQ0TeN5/vj4+PQLz2BBEAzDkCTJNM1cLle31fsrilIvL8uyZVnnmCVJql+y3rO+S5IkimJ9lmVZkqRCoWBZVrlc/jf8DyDwG3bdqLUQAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/567974480961b291ddbe03ffcdbf701c/8ae3e/ex_4_2.png",
                  "srcSet": ["/version/4.9.1/static/567974480961b291ddbe03ffcdbf701c/a2ead/ex_4_2.png 259w", "/version/4.9.1/static/567974480961b291ddbe03ffcdbf701c/6b9fd/ex_4_2.png 518w", "/version/4.9.1/static/567974480961b291ddbe03ffcdbf701c/8ae3e/ex_4_2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            Don’t wrap text. For maximum legibility and recognition as a button,
            a text label should never wrap to multiple lines.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "111.96911196911196%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAWCAIAAABPIytRAAAACXBIWXMAAAsTAAALEwEAmpwYAAAD80lEQVQ4y3XQyW8bVRwH8PwNvSBRL7N5iZI4y8QUkKjjJC5KHHtmPLudRaISopwQnCgcKGmcuOHSA2RTEULijpQikrZHBBJCCIGqnBLJkWc8b8bj2RxT7NiDvIRQVL56+uldPvr+3hvgKWqJJN7Kiiu53FL35LJZnuNomiZJMpUiF5JEcp5IJolkklxIkq+/Ftve3nVd17btAY5I3UzOf0ATb/PcCscJHMtkOiydJlIpIp2iKIphGI5huAzNUhQTi83s7j7oY+FG4v0b058Tc7c5+ibPLQq8KPACL/wnPM8zLEvTTDye2Nv7so9vTce+iUf+4PGv+dQtgV/JCqIgiBcRBJHjRZYTaUagMhxJsVPxxPbOXh+/c33qFw52H197wo68G43yZDq3mOuyTj3HCSwnMqxIs5d4a2unj+lA4O4r8FdLr34UgZcx/1wosDAdFwQxt5jjhU4ny2UZVsxcNMemEltbFx82DsFjHigD+96A4GEImYThazA0NhyZnp6lMjSVYQmSptlOP0mxaZKOTc1+8U8zBqERCE6EkSE/HIYQBEIxOID4EL8XCQYGw+FhDAtHIhOx+Oz8AkGQ9MxMYnvnohnxI4gfhrwQ7IMgL+z3wl4P7PUgPi/iuQpdfRnyeuDOxQNBcGBoaNTnRwuFzT4OoKEgGgyhwSAaxNAQhoZQNNydIQwNY9gghg5i2CCKDSJoGEXDV668dOfO3T7GJ/DU6Mh74+HU2GgUx3F8EsejE3h0bDw62jtj0cjo5HAEHxqZGI7gHh+SXy/0cX5t7f7q6oNPP7m/uppf6ySfz+fX85ubhc3PCp25Wbi3uVG4t7FRWN/YWP/w9sf7D787Pz93HGfA/Z9Uqu0yaAGtBdRzoLYUtQXUll5tt1230Wg4jnN2djZQKpUkSTo+PgYAyLJcKsmu6/7wo5RI7fPLj9Lc95R4QGcP2KVDJnd4/c1vf/1NbbWe2XYXF4vF09PTo6MjSZJKnUiu6/70szKbesgtPya4A1I4THMHmewjdunJ1Nz+709112308Qt3brdd02rq1UbVaOjVv6pGs3cxrWar5dbrdcepdbCmaZZl2bZtWZau66Zp1ut10zDOalatZj77s9Zo1C1Ttyy9fmbVHMt1O7hW6+JyuayqqmEYAACtG13XK5WKoiigG03TTNOsVqu2bRuG8RzWNK1YLBqGUe1G0zQAgGVZlUpFVdVyuawoSrvzx5e5xCcnJ4ZhyLJcLBZt2wYASJLU87Ism6ZZqVQkSWo2m92/aD+HZVm2LEtRFNu2dV3vtfX2V1VVURRVVQEAjuO8oFlRlN4je3v2pqIo5XK5dwcAKIpimqbjOK1W69/4b5PhJLJHtyZkAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/8f48f8019a86f14d444137a914d0bc2a/8ae3e/ex_4_3.png",
                  "srcSet": ["/version/4.9.1/static/8f48f8019a86f14d444137a914d0bc2a/a2ead/ex_4_3.png 259w", "/version/4.9.1/static/8f48f8019a86f14d444137a914d0bc2a/6b9fd/ex_4_3.png 518w", "/version/4.9.1/static/8f48f8019a86f14d444137a914d0bc2a/8ae3e/ex_4_3.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Text labels can be written in title case if all efforts to make the
            label less than three words have been exhausted, or if the button
            simply won't fit in the space without making it smaller.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col"
          }}>{`
  `}</div>{`
  `}<div parentName="div" {...{
            "className": "row"
          }}>{`
    `}<div parentName="div" {...{
              "className": "col col-1"
            }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                  "className": "gatsby-resp-image-wrapper",
                  "style": {
                    "position": "relative",
                    "display": "block",
                    "marginLeft": "auto",
                    "marginRight": "auto",
                    "maxWidth": "756px"
                  }
                }}>{`
      `}<span parentName="span" {...{
                    "className": "gatsby-resp-image-background-image",
                    "style": {
                      "paddingBottom": "58.301158301158296%",
                      "position": "relative",
                      "bottom": "0",
                      "left": "0",
                      "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAIAAADtbgqsAAAACXBIWXMAAAsTAAALEwEAmpwYAAABV0lEQVQoz52Py67jIAxA+/8f2MVM0qYQqGNIXGKnBfLoKKFSN3c29wghbHH8ODFzSomIlFIAUB8YY5qmqarKOWetresaAO73u9a6aZrr9dp13fl8PolIPEBEIur7HhGZGQCYuWScc0Q0jiMiWmunaer7vuu6XU4pTdNkjAEAOhiGAQAQ0XvvnCvOOI4AYIzx3ocQvPefziklpZQxhohExHtPRABQ2jrnSt1Sy3vfH3w6M3PbtmXDEAIiikhxRKQ7MMaEEIoGANba79iXy6Vt98mVUtbaYRjatrXWaq201kSEiM65sm0I4Xa77fLr9dq2LaW4LHld55xiSnFdl2XJ85zmOcUYc87poDzK/ZHf73fOK8vMMj9fa85bjAvLLEdm294/ssvP5y7TI/6pHvU1aM3GCvpXVYeb4r/VI+f1v3Lp/AtO0zSN4ygizN/zDeUT/sguH19/wz8W8Kbj9DkK2AAAAABJRU5ErkJggg==')",
                      "backgroundSize": "cover",
                      "display": "block"
                    }
                  }}></span>{`
  `}<img parentName="span" {...{
                    "className": "gatsby-resp-image-image",
                    "alt": "",
                    "title": "",
                    "src": "/version/4.9.1/static/782a58f854e93557c59adf10051ebb52/8ae3e/ex_4_4.png",
                    "srcSet": ["/version/4.9.1/static/782a58f854e93557c59adf10051ebb52/a2ead/ex_4_4.png 259w", "/version/4.9.1/static/782a58f854e93557c59adf10051ebb52/6b9fd/ex_4_4.png 518w", "/version/4.9.1/static/782a58f854e93557c59adf10051ebb52/8ae3e/ex_4_4.png 756w"],
                    "sizes": "(max-width: 756px) 100vw, 756px",
                    "style": {
                      "width": "100%",
                      "height": "100%",
                      "margin": "0",
                      "verticalAlign": "middle",
                      "position": "absolute",
                      "top": "0",
                      "left": "0"
                    },
                    "loading": "lazy"
                  }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                    "className": "title title-correct"
                  }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Text buttons don’t typically need a container when in all caps,
            because the label is differentiated from the text around it.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
              "className": "col col-2"
            }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                  "className": "gatsby-resp-image-wrapper",
                  "style": {
                    "position": "relative",
                    "display": "block",
                    "marginLeft": "auto",
                    "marginRight": "auto",
                    "maxWidth": "756px"
                  }
                }}>{`
      `}<span parentName="span" {...{
                    "className": "gatsby-resp-image-background-image",
                    "style": {
                      "paddingBottom": "58.301158301158296%",
                      "position": "relative",
                      "bottom": "0",
                      "left": "0",
                      "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAIAAADtbgqsAAAACXBIWXMAAAsTAAALEwEAmpwYAAABTElEQVQoz53P2W6EIBSA4Xn/R+w4RaPHc6CALKIsLs1gm+lN06RfCBeQn+XmvY8xhhAYY0IIxljTNH3ft23LGAMAIcT9fuecA8AwDG3bdl2HiI/H4zbP87quOWcp5VRxzr334zhaa68VKaXW2lqrlCIiWxHRV5xSIqJxHKdp0hXn/KPSWiPitaWUAgAiMtVXvO87InZdp7V2zimlrLVCiOtOIhJCXOd+fLPW3q4/r+sKAJxzIYRSSkrpvVdKIaIxZpomRCQipZQxRkrJOUfEV9xXADBW1wuHYUDEvu8551JKIYS1FgCMMQDwfPayLOd5lpLqyKXknOJxbFvJKcVLSinnnFIqpcQYc/WK13ULYYtpn8O2LJufyxr3Uo7zd68YKTQPCzi3nRsxvDXmnbnJpPM8j+Ov+B9uIQTnnPfeOe9+zs+1PzzjeZ79v3wCbxOnpNmVgxkAAAAASUVORK5CYII=')",
                      "backgroundSize": "cover",
                      "display": "block"
                    }
                  }}></span>{`
  `}<img parentName="span" {...{
                    "className": "gatsby-resp-image-image",
                    "alt": "",
                    "title": "",
                    "src": "/version/4.9.1/static/0e15d467d9ba60f9715b4e09bf36039e/8ae3e/ex_4_5.png",
                    "srcSet": ["/version/4.9.1/static/0e15d467d9ba60f9715b4e09bf36039e/a2ead/ex_4_5.png 259w", "/version/4.9.1/static/0e15d467d9ba60f9715b4e09bf36039e/6b9fd/ex_4_5.png 518w", "/version/4.9.1/static/0e15d467d9ba60f9715b4e09bf36039e/8ae3e/ex_4_5.png 756w"],
                    "sizes": "(max-width: 756px) 100vw, 756px",
                    "style": {
                      "width": "100%",
                      "height": "100%",
                      "margin": "0",
                      "verticalAlign": "middle",
                      "position": "absolute",
                      "top": "0",
                      "left": "0"
                    },
                    "loading": "lazy"
                  }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                    "className": "title title-caution"
                  }}>{`Caution`}</p>{`
          `}<p parentName="figcaption">{`
            Text labels need to be distinct from other elements. Without a
            container, the button above could be hard to distinguish from the
            content above it.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
        </div></div>
      <h3>{`Icon`}</h3>
      <p>{`You can place icons next to text labels to both clarify an action and call attention to a button. If a text label is not used, make sure the icon is intuitive and clear through testing. Also, consider using a tooltip so its exact meaning can be easily seen on hover.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAABG0lEQVQoz2P4Twr49+/f////nzx5kpOTk5aWxvCDFPDt2zeI5vT09MTERIa/pIDfv3//+fMHoZkkZ0OMGAyaf2MDP37++v7jJyb6+u37799/CGj+9+8vnqh6/PgxdmeDZf9fv/lhzYZ7G7Y8WLfp/rpN99dvBpOb7q9ae/fu/a8vXjxNS0sDaf6HCv78AdlZXHncL2yHf/gOa+cNdm4bzR3WWziud/ffqqq/vKn9wqePL5NTUrHaDLJ6196n9a3nOvsutfVcbO2+0Nx5obX7Qmffpcr60wePvH779kVaWnpCQgLDW2zg06f3Hz8i0CcI48P7jx/fff/+9fz5CykpKSCbU7CB5GScKCUlJSkpKTExMSkpiSGRLJAEBgD8JKPh5xcpKAAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/bce7a5ca844498dd226b1f37da6d6866/8ae3e/ex_4_6.png",
                  "srcSet": ["/version/4.9.1/static/bce7a5ca844498dd226b1f37da6d6866/a2ead/ex_4_6.png 259w", "/version/4.9.1/static/bce7a5ca844498dd226b1f37da6d6866/6b9fd/ex_4_6.png 518w", "/version/4.9.1/static/bce7a5ca844498dd226b1f37da6d6866/8ae3e/ex_4_6.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`Use icons that clearly communicate their meaning.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAABK0lEQVQoz5WSwUoDMRCG9418BY+CCkIF38CbB28iCvoGXrz5FEXUs3sQF/Rga5faLV7cakXjarvb7GQykWzsbk1jqf9hyAzzZTI/8dQcIqripDwd4EGlZ4rATRbxawAA0gFnT7v92wUFN0WfLIZo6UTq2GyxlfWLo+NGWalgyq4FO1FyUE76A75zwJYAwPf9MAwnF2YfPM9Rn1CW23vjaZIkYXErAARBEEXRlGHkMsxhL1mtJpOcZ82GeO2bUgXHvbTdSYSQTlhJXU9O61FtNT7Y+4FND+e4uXW5uFS/bzHLlTGMBu5urMWH+7/gHOT2ztVy7bzdSSxYIJUpCcEfu/iZ2M8eDoExbj1WCHp+yd7eudMab/aXHI0w7qWcY+Ukotl/Fjx90T8mz6NvGVgoB3cKqYgAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/353bb4d8a3ee48f676404b48712c96a7/8ae3e/ex_4_7.png",
                  "srcSet": ["/version/4.9.1/static/353bb4d8a3ee48f676404b48712c96a7/a2ead/ex_4_7.png 259w", "/version/4.9.1/static/353bb4d8a3ee48f676404b48712c96a7/6b9fd/ex_4_7.png 518w", "/version/4.9.1/static/353bb4d8a3ee48f676404b48712c96a7/8ae3e/ex_4_7.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            You can use icons without a text label if testing proves that the
            intended action of the button is clear to the users.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAABM0lEQVQoz2P4Twr4CwZPnjzJyclJS0tjIE9zenp6YmIifTX/+fMHofkHDvDt23dM9OXL1z9//iE0/8MBsNoMEX/8+DFOZ0M0zph7LSnrYE7x0azCI5kFh7MKj+QWH01I379159O3b56npqbh05yRf1jdYKWu+Wob1411LWcMLNcYWK1V1lnWN/nqxw8vU1Lwai6sOGblvME7eIdX8PbIhD1uflv9wnaY2q2bMuvGh/cvCGhOzDhgbLMuNGa3k/dme49NHgHbgqN3Kesu6+y/ArY5FZ/m5avvlVSdqmo4U9VwprrxLIRRVHnywJHXb9++SEtLT0hIYHiLA3z69P7z5/dfPn/4DENfPn/4+PHd9+9fzp+/kJKSArI5BQdITsaOUlJSkpKSEhMTk5KSGBLJAklgAAADHoX2Fgqk3QAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/853ff5d31d5040f43d9d7ccb3bb46244/8ae3e/ex_4_8.png",
                  "srcSet": ["/version/4.9.1/static/853ff5d31d5040f43d9d7ccb3bb46244/a2ead/ex_4_8.png 259w", "/version/4.9.1/static/853ff5d31d5040f43d9d7ccb3bb46244/6b9fd/ex_4_8.png 518w", "/version/4.9.1/static/853ff5d31d5040f43d9d7ccb3bb46244/8ae3e/ex_4_8.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            Don’t vertically align an icon and text in the center of a contained
            button.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAABOUlEQVQoz82Ry0rDQBiF80iuvGzU4kLQl8hMppNNXkFQKqaKxcdwoU1oU8Gl4r4X16WJtELEpElTcrEzycg0iFZoIa78OAyHH86cH36BFSHLMsbYaDRSFAVjLCRFiKIoD8uyDAAQ0iIQQiil3+FCa+df/IcwWWQ2m8XJxzKFUUwIXRqmlOZHWXGq4XD4e+35nDluohtWo2XVm6bWMHXD0ppmfW50w7q+6Q/M0LZfMcY8nH1BSMoYu7t/Wd/Rysrj2tbt3mFrY1fbLOmlAyN/t/cbR5X2NHiTUHmhOU15dX8QHJ+1z6+eTy+66mWvUu1yU+tVqh211jtR2w9P765rYyyLoii4P3Ac1/fH08CfTDwu3wsCbnx/HPDJ2PPcOA47nS5CiDejRSSEJGmVEEIQQgAAhFAAfwLO+QRr7J3jZM2BFgAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/112aedaf78177597a02e53ef65ea6115/8ae3e/ex_4_9.png",
                  "srcSet": ["/version/4.9.1/static/112aedaf78177597a02e53ef65ea6115/a2ead/ex_4_9.png 259w", "/version/4.9.1/static/112aedaf78177597a02e53ef65ea6115/6b9fd/ex_4_9.png 518w", "/version/4.9.1/static/112aedaf78177597a02e53ef65ea6115/8ae3e/ex_4_9.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`Don’t use two icons in the same button.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
      <hr></hr>
      <h2>{`Sizes`}</h2>
      <p>{`Other than the standard sized buttons, there are two other sizes – Large and small.`}</p>
      <p><span parentName="p" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "22.77992277992278%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAFCAIAAADKYVtkAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA1klEQVQY02P4TwFg+PX77+cvv798/f3p088/f/6+fvP97IXXl668vXDpzflLby5defvl6+91G+/NXXhj0bJbC5bcnL/45sKlN2fOvXbrzgeG3fseB0fvSs0+FBS1a82Ge6vX3bVy3hgctcszcFtAxE7PwG2nzrxy9NpsYrvOzGG9lctGI5u1rn5bxZUWLVh6k+Hnzz9v3n5/9/7Hm7fff/788/Llt2MnX5w68+rUWRA6c+7V58+/Vqy9O2PutZnzrs2ef332guvzFt+YPOPK9ZvvGSjxMwAMfvn1Maqa3QAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": " ",
            "title": " ",
            "src": "/version/4.9.1/static/7d05453af8e3b2af6e65ec0efc6e4bb3/e3189/button-sizes.png",
            "srcSet": ["/version/4.9.1/static/7d05453af8e3b2af6e65ec0efc6e4bb3/a2ead/button-sizes.png 259w", "/version/4.9.1/static/7d05453af8e3b2af6e65ec0efc6e4bb3/6b9fd/button-sizes.png 518w", "/version/4.9.1/static/7d05453af8e3b2af6e65ec0efc6e4bb3/e3189/button-sizes.png 1035w", "/version/4.9.1/static/7d05453af8e3b2af6e65ec0efc6e4bb3/1843f/button-sizes.png 1186w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span></p>
      <p><em parentName="p">{`Large`}</em>{` buttons can be used in scenarios where space allows and you desire a much more prominent call to action.`}</p>
      <p><em parentName="p">{`Small`}</em>{` buttons can be used in scenarios where you want less emphasis on some actions because there are other more prominent actions on the UI. They can also be used when space is limited for the number of buttons you need.`}</p>
      <p><strong parentName="p">{`Minimum Width -`}</strong>{` Buttons with extremely short labels like “OK” or “Save” are in danger of being very small, even though they may be the primary action. To avoid this, all text buttons have a minimum of width of 5.625em (90px). This does not apply to icon only buttons.`}</p>
      <hr></hr>
      <h2>{`Button Colors`}</h2>
      <p>{`Color can help reinforce the type of action that a button will perform if tapped. The default colors of the buttons are carefully chosen to both fit Cengage branding guidelines, as well as meet the accessibility requirement for color contrast ratio. If deviations to these colors are required, then you assume responsibility to meet accessibility requirements.`}</p>
      <h3>{`Primary and Secondary Buttons`}</h3>
      <p>{`The `}<strong parentName="p">{`primary`}</strong>{` button should be used by default for important actions. From there you can use `}<strong parentName="p">{`secondary`}</strong>{` or buttons without a container for other actions, because they less emphasis than primary buttons.`}</p>
      <p>{`When you are pairing together primary and secondary actions such as "Save" and "Cancel", it is recommended you use the primary and secondary buttons.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAYAAAAvxDzwAAAACXBIWXMAAAsTAAALEwEAmpwYAAABYUlEQVQ4y62TT0sCQRiH94t06NShT9Ex6NhFXQ87I+i1W3SIIA/1NYKOSQrZIQg6BK5bWaBbQbuS5V9Q0tVdV3bnF7ObiGRkay88zPAeHt7fvIxgmiaCYhgGXNdFuVxmiUQC8XicCVigGGMemqYzSikkSWLCuBkEx3F8oa6zWCwGQkiwCbmEF4/7TRh0uh+Fi7whF3KmhJZlYV5M08JgYHr4W+57Qn3eyLPebVa9aNrvkS3LQbdro9cbod0ZwrZdVN772NmTsZtUsH9wi63ta1xeNVGrvjJCqC/kq5+FaY7won9AfWrj8bkDw7CRv2liefUYa+sZbGxmsbRyhOThPVrNCpMk+velDG3g/KKB9FkVmWwNJ+k31BtAq1VnlH5FzskycnIek9O/y/lxb4KiKCiV7qCqBailgncvFh+QSp0yQggopUwQRRHzEomICIUiU4TDIqLR6ETIt/MPeH+ZR/4ELSm+Ud6V14UAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/436cc7a88d954f704a600e356bcb0dfc/8ae3e/ex_6_1.png",
                  "srcSet": ["/version/4.9.1/static/436cc7a88d954f704a600e356bcb0dfc/a2ead/ex_6_1.png 259w", "/version/4.9.1/static/436cc7a88d954f704a600e356bcb0dfc/6b9fd/ex_6_1.png 518w", "/version/4.9.1/static/436cc7a88d954f704a600e356bcb0dfc/8ae3e/ex_6_1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
  `}</div>
        </div></div>
      <h3>{`Subtle Button`}</h3>
      <p>{`The `}<strong parentName="p">{`subtle`}</strong>{` button style is intended to have even less emphasis than `}<strong parentName="p">{`secondary`}</strong>{`. For this reason, it is recommended for ancillary actions that need to be seen as clearly actionable, but don't draw attention away from the primary and secondary actions on the page.`}</p>
      <p>{`An additional benefit of the subtle button's very neutral appearance is to help limit the amount of the primary brand color appearing on the page. Too much of this color can result in a very chaotic experience where everything looks relatively important and is distracting. The subtle button helps calm an interface that has a high number of actions that can be performed.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAYAAAAvxDzwAAAACXBIWXMAAAsTAAALEwEAmpwYAAAB50lEQVQ4y41Sz2sTQRTe/8ij6EnQiwcFETwo9CCKSEWl4B/gyV78JzzUgx56EBGJTWK8BJGyhKRuuq3d1o27miY7+2tmf2Q+ebs7SazQ7sDH9715b968eW80zjk4jxHH8ZyXtfILwSE4L/l/yOqc1EpROvjSAaVV0iCICjAWwvejAqQrW1axlFBgMhVw3AgeK/n3nxjHE17oPM/xamMHt+82cG+1hUdrHTx8+hkPHrexutbBk2dfsHJ/S75+YwDIpCZEAseNYe6zgo1dD+Yeww8rwPehBymB5y++4tyFt7h4eRPXb33A1RvvceXau0LfvPMR5y9tyvWX3wDkUgvDGFEkwHmJJElAl5BWvLd/jEbzAK3OIT61LGy1LDTblW4fotG05IE1QZoKqVHDF8OI5j1TwyFkmaDbK2QnmHypzLNkMRSqyjRNGIYBXddhWRY8j8HzPGRZhqOjn9D1HgaDHfR6fQyHu9je1tHvDzAa/YJtj6QQYnnKMcIwRBAEYIwVTHtRFM2Z9n3fL1iBbDrHGFt8G5oiYTab/QO1r3ynLSnlokLUWGmawrZtOI4D13UxHo8Lm3S32y0SUUyRUEqJ00CL+jidTufPpJYom/qdpqmkOdSqUCU9K6zWk1WyGq+oX+FZF55M+Bfc/CCpQqR0ZQAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/3ff1d360e3458e66d03c40efe883233c/8ae3e/subtle_button_example_1.png",
                  "srcSet": ["/version/4.9.1/static/3ff1d360e3458e66d03c40efe883233c/a2ead/subtle_button_example_1.png 259w", "/version/4.9.1/static/3ff1d360e3458e66d03c40efe883233c/6b9fd/subtle_button_example_1.png 518w", "/version/4.9.1/static/3ff1d360e3458e66d03c40efe883233c/8ae3e/subtle_button_example_1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAYAAAAvxDzwAAAACXBIWXMAAAsTAAALEwEAmpwYAAACD0lEQVQ4y6WTu24TQRiF8zK0qVIhpU4DDc/AA6B0UCAhUSDokGiSgjegpiAiLbdEiY1tHMEm9sbrvc2u937/0MyycWIMFIz06/zzr+bsObNnN6o852Qe8OHtGw72nrF3/x6vX73ELMCzTRZBgOu6BEFAVVXI1TSNqq5vocWNpir5qM3Zf/GY/d0HPN/a5OnDRzg1BMJRRI7jsFj4lGVLuLo6NkVIU3M4mrC5vcPW9l1u3d7hzu4TRAn6NGRupuizBGOecDENmRkxk2nI9DJCOw9IkvKmwjxNCYqa95OAgwufd7OUIydTVvK8oiiuV01Z1gqLsu3resVykiQS+Z91w3JLiLLw6cjm6NShPxT0B4IvxzanfZfPx7baD0Yeva+C4xObxSJf/1E6wijKsKwQIVJcN8H3Uxw3wRUJjtv2wpMYI0SmbP+VUAiX0WjA2dk3Ls411f/4fsZg0Mc0DXxf4Dg2tm0Sx+G/Ldu2w3A4Yjweo2nn9Ho9NE1TkTGMObp+ied5RHGMYRiYpoWu6ypWaxUWRUEURch9mqaEYah6OS/LkjzPFcpwy5msLMu6sDe/wr4klAcsy1IqWgUmQgh1UO5bpe1c9qsf+jfL8u2dQomy4jimKErCsO27may6rq9+waZdS4XLO12bsrVTafVaNR1eKQyjAs/LVEQ8P8OyZUwyVa7I1Ew+My15x9UfLf8E6xooSpwK+v0AAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/056ab700763ad94d0adcd24e9db943dc/8ae3e/subtle_button_example_2.png",
                  "srcSet": ["/version/4.9.1/static/056ab700763ad94d0adcd24e9db943dc/a2ead/subtle_button_example_2.png 259w", "/version/4.9.1/static/056ab700763ad94d0adcd24e9db943dc/6b9fd/subtle_button_example_2.png 518w", "/version/4.9.1/static/056ab700763ad94d0adcd24e9db943dc/8ae3e/subtle_button_example_2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
      <h3>{`Danger Button`}</h3>
      <p>{`For actions that delete things, you can emphasize this action with the `}<strong parentName="p">{`danger`}</strong>{` buttons.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA80lEQVQoz9WSzUrDQBSF54FcVRARwYVVsLi1CUKSRd4hz2P+pk4fp1CVgAstRlv/MelMprk9JVAobkxn6cfh7g733B8GQ4gIAOfccRxGhtR1DSCOY9u2WWVIWZYAkiRpzKaxlVL/xrxcrgVIKQGkadqYVStSKq0roqqmimiutVlnKn50ni9mU/2c68+Pzala0gKk5P3lRXZ2nPW62fnp7ckR3t/S4dC2LPb3QxCwKIq7Xnd8sHtzuDfe74w6O5i+rM2tu2rK9xcmD8ifMHmk19lm5qstCDmPhIiECK9FOBhEURQEged5zNqGfv+XLMt1Xd/3V83YzCfxFMVPAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/deea15c93948765270331dccb894fb7c/8ae3e/ex_6_3.png",
                  "srcSet": ["/version/4.9.1/static/deea15c93948765270331dccb894fb7c/a2ead/ex_6_3.png 259w", "/version/4.9.1/static/deea15c93948765270331dccb894fb7c/6b9fd/ex_6_3.png 518w", "/version/4.9.1/static/deea15c93948765270331dccb894fb7c/8ae3e/ex_6_3.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
  `}</div>
        </div></div>
      <h3>{`Marketing Button`}</h3>
      <p>{`The `}<strong parentName="p">{`marketing`}</strong>{` button is intended to be used on product websites where the primary purpose is to market and sell our products. The button is meant to be loud and draw as much attention to it as possible. For this reason, it is not recommended for use within the platforms and applications where our customers are trying to complete relatively complex actions. We want these experiences to instill a sense of order and calm, and this button is meant to disrupt those feelings.`}</p>
      <p><span parentName="p" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "33.59073359073359%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsTAAALEwEAmpwYAAABy0lEQVQoz1WRz2sTcRDF9+JFEKmQi0oP9WKrKPXoQcFTaYIXbUEP2jYVrLTUpkkRUhXBf6AevQfBSKugSSrFgidrKTZglYCpSkjIZne/+yubjZJ8P2W3Kvjg8WbmDY+BUTy/jdtq43g+rufTbLVp+b/+1YEfaEApJZ1OF2H7bG5tUci/oZDPkc+9Zm1tlbeFPIoqHDSrScN0qek2uu1h2B7lqhbODMdDFS7C8QiwW/5GPD5OX99xeo9FOHzwAJGeQ5w+dZLIkR4Ux7axTBPbMjGFhjA0TKFjmTq2GajA0HWEMMLAYrHIcCzK3cQCmWevSC8+YvHBY4aGLnPxbD+K6zap1+sYwqFStSh/V/lZ0dn9oVKpmojg8loNy7bDwO3tT8SiUSZvTTGXTDM5EWfpyVOuj4wydSWGwl/IBm39Oc1GFq+RxVWzYY+s/vG7oXzc+MCZgRP0Ho0weG6QSxfOcy+V5GFylqXUbRQpO8hg39/E/BpD2xlB37lK4/Mo4ksM6a2HfrfzOwwslUpMjI8xdvMGMzPTJOYTpJLzTM8tcGf2PgpS7h8gO4ALOP9xf0744QCqqpHJrJDN5lhZXmX55Tver29wLf2CgeE0ew5byHMv4tNuAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": " ",
            "title": " ",
            "src": "/version/4.9.1/static/e7bc5d46c6fa7fc859acd393ef03cd27/e3189/marketing_example.png",
            "srcSet": ["/version/4.9.1/static/e7bc5d46c6fa7fc859acd393ef03cd27/a2ead/marketing_example.png 259w", "/version/4.9.1/static/e7bc5d46c6fa7fc859acd393ef03cd27/6b9fd/marketing_example.png 518w", "/version/4.9.1/static/e7bc5d46c6fa7fc859acd393ef03cd27/e3189/marketing_example.png 1035w", "/version/4.9.1/static/e7bc5d46c6fa7fc859acd393ef03cd27/44d59/marketing_example.png 1553w", "/version/4.9.1/static/e7bc5d46c6fa7fc859acd393ef03cd27/5eb90/marketing_example.png 1612w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span></p>
      <h3>{`Inverse Colors`}</h3>
      <p>{`If your button is going on a dark background, all of the buttons have an inverse appearance for optimal accessibility. They are carefully designed for use within entirely dark themes for Dark Mode.`}</p>
      <div {...{
        "className": "one-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "1035px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "31.27413127413127%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAYAAADDl76dAAAACXBIWXMAAAsTAAALEwEAmpwYAAABbUlEQVQY03XPyUrDYBQF4DxIFYVihyR/0vZPamsUFVcuLIIiWHHAisWlG4cMf6pttVRRUHBCBBd16QCKLhXxOVz4HPdIoqAILj7u4azOlabLLVw9fODy9h2be6/QDB+5/gbyAw0YVg1yykO2t4qe/i1o3EPrZAZv90W8XBfxdDONlcFJ3PFBPPYOoc5GII1MnMLfeYWz/Yzy8jXUjEA6XwO36kjnqpBTLnh+A6ZVhZpxUVkr43h3AUfNEvaai5ixSmjyMeznxrHEipCiyVWoGS9cFmRFd5DrFjAMFzF1HXFmh7dL+crtXR4i0S9tUQ+dqkAkoAh0MAEpzmzSjArp2U2KMYdk3SHTFJTiHiWYTfE/krpN8i9JzSb5W5ClvqEG5hcvMFs6x9TcWfiyygX0bAWMCyQ0B7rhI2X6UNJuuDLoEpr9g/2QTKtKo+OHVBg7oOHCPilpjzQuSDf9MCc0h9SMR4wLSupuuDLo/vMJHSbvyba/ETcAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/5c234d05000c998c02796694133af412/e3189/ex_6_4.png",
                  "srcSet": ["/version/4.9.1/static/5c234d05000c998c02796694133af412/a2ead/ex_6_4.png 259w", "/version/4.9.1/static/5c234d05000c998c02796694133af412/6b9fd/ex_6_4.png 518w", "/version/4.9.1/static/5c234d05000c998c02796694133af412/e3189/ex_6_4.png 1035w", "/version/4.9.1/static/5c234d05000c998c02796694133af412/46115/ex_6_4.png 1290w"],
                  "sizes": "(max-width: 1035px) 100vw, 1035px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
      <hr></hr>
      <h2>{`Button States`}</h2>
      <p>{`Buttons have clear states to provide feedback to the user.`}</p>
      <div {...{
        "className": "one-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "1035px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "32.04633204633204%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAIAAABM9SnKAAAACXBIWXMAAAsTAAALEwEAmpwYAAABXklEQVQY0yWQvUvDQByG8y+JgggOgiC4CE7iIE5OgiK4OArOOqiDCOLo4scqalWqS62mFrVN2tKkxqb24l2au+R3l1zv/Hrn54GH15AyoxFJU9Bap6mgNFRKaa2F4ACx/hsHlqZca62UIoRgjP8ZAyB7eW1XrcBrJwhFz2XHrnX9TvTRRmapZtneZzd8sf1CyS1XPzsBIwQjhABAKWVYdjg9ezE1cz63cLN3UBif3B2b2F5cOVnfOBoaWRkcXl5dO5xfOh6d2BkY3dzav3fderH4aJomY8xIkqxUDp5K6LVC/E5UKLqFh5ZVQ02nm7+r3N1Xao1O3vTOcvbplV11voQASiljTEppKKUBZNgTSZIl0G95UdMNMeHue3x57eRuW9V6z/LoWx1XGuQDc/53hhDiN7vfV3GSxXEGXHIuMQGMgbGUhLzphm6rF3zBO4odnzZ9GkRCSpllUkr5I38DnDU2a73vQwQAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/910ad0f808217a6516222c83dca50f79/e3189/ex_7_1.png",
                  "srcSet": ["/version/4.9.1/static/910ad0f808217a6516222c83dca50f79/a2ead/ex_7_1.png 259w", "/version/4.9.1/static/910ad0f808217a6516222c83dca50f79/6b9fd/ex_7_1.png 518w", "/version/4.9.1/static/910ad0f808217a6516222c83dca50f79/e3189/ex_7_1.png 1035w", "/version/4.9.1/static/910ad0f808217a6516222c83dca50f79/35252/ex_7_1.png 1204w"],
                  "sizes": "(max-width: 1035px) 100vw, 1035px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
      <h3>{`Focus`}</h3>
      <p>{`It’s important for users to clearly see when the browser’s focus is on a button. This is especially important for users who can’t use a mouse, but instead have to rely on a keyboard or other means for navigating through the elements on the page. The default focus presentation built into the browsers are not consistent, and have varying success in visibility depending on what’s behind the button. For this reason, we are putting our own custom focus effect on Magma components.`}</p>
      <p>{`The solid outline is 2px thick with at least a 2px offset, and the color is info-500. These properties ensure the outline is visible on a light background. The color changes to semi-transparent-white for optimal visibility on dark backgrounds.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "604px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "32.04633204633204%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAGCAIAAABM9SnKAAAACXBIWXMAAAsTAAALEwEAmpwYAAABL0lEQVQY02P4jw38+/fv////37//cnLvVlQrU1IvU1QrVVAFIUU1EFLVrtDUr2H4////lQcf+hdf7190vW/htd4F1yYsut6z4OqpG+/+/Plj69Ru7z2xuudQdfdBENlzqKrrYE3vYb/ouaralSDNU5fdsHTcEJW0Nyx2T2TC3qikvSbW6xomX/j//7+lXWtBw84LD/9vPPByy5HXmw6+2nTw5eUn/7vmnFFQKwNpnrHqlontOq/gbb6hO/zCdngHbze0XNM87RJEc27ttstP/u8//2XPmY97Tn/ce+7z1Wf/22eeVlAHa77z/MuirfcWbbm3cPPdhZvuLt5yb8Hmu1cefvr9+4+dc4elS0929dbsqq0gsnprVtWW7JptbkFT1XTAziYQYOrl2AKsEhhgAGlW5Adx/m5HAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/b37716c2d9a60125aaa28dfeb1e190c3/87254/ex_7_2.png",
                  "srcSet": ["/version/4.9.1/static/b37716c2d9a60125aaa28dfeb1e190c3/a2ead/ex_7_2.png 259w", "/version/4.9.1/static/b37716c2d9a60125aaa28dfeb1e190c3/6b9fd/ex_7_2.png 518w", "/version/4.9.1/static/b37716c2d9a60125aaa28dfeb1e190c3/87254/ex_7_2.png 604w"],
                  "sizes": "(max-width: 604px) 100vw, 604px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
      <hr></hr>
      <h2>{`Hierarchy and Placement`}</h2>
      <h3>{`Hierarchy`}</h3>
      <h4>{`Single Prominent Button`}</h4>
      <p>{`A layout should contain a single prominent button that makes it clear that other buttons have less visual importance in the hierarchy. This high-emphasis button commands the most attention.`}</p>
      <h4>{`Other Buttons`}</h4>
      <p>{`An app can show more than one button in a layout at a time, so a high-emphasis button can be accompanied by medium- and low-emphasis buttons that perform less important actions. When using multiple buttons, ensure the available state of one button doesn’t look like the disabled state of another.`}</p>
      <h3>{`Placement`}</h3>
      <p>{`Multiple button types can be used to express different emphasis levels. When using buttons that are contextually connected to left-to-right content, the buttons should also be left aligned, with the most important action coming first. When the buttons are positioned as actions for a larger context, like an entire modal or page, then the buttons will typically be right aligned, with the most important action on the far right.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAACJUlEQVQoz3WSz2sUMRTH+295Ew+KP1AsCB68iUKxhSoePfmHePAvEKEWPHho66m2rgeVYted2c1MXvKSTJLd7M5MMhOZmeJS0C/h8RLy4b2XfDecc/P5PISAiAAghEiSBAAYY3meZ1lmjMnzHBGllJzz2WwGAIhIKd34C1trEVFrzRgTQkgpAaAoCqUUIgohtNZCiKHAcO0C9t475wBASqm1VkoNdYpe2EsIYa2llHLOtdZFUaxha+1kMiGEGGOklIiolJpOp0VRZFlGCGGMKaUopdBLSrmGjTGMMc656mWMGQYRQiwWiyFBRGutlLIoinXb/YOJ8XgynZIkmRKSUcqynP6eJHkOhBAA4JwzxgYMEddwXdexk4+xiTHEWPexjdG3rV+tVlVV1VVVlmXdqyrLqiw72Np503hgq+NTPBnhyUicjkQXv4kvX/HnmW7b+E91sNY2xvjq9fGVa+/uP/x458H+jXt7tzf3r9/du7X54erN91LXceWWP76Xk3GVplU6Kcfn5fmvDjbGxti+eXv26PGnnZefnz0/2to93No9fLpz8GT7YPvF0XzhYxO8ksGaxrlgbTO3wdpLM7uldy4457u19Mtll3vfxPj/tge4aVrvQwih6RVC40PwPvSH3T5438U+8XXtvb/ksME9gxOUUpxzANBaDx/DGBtyABiscgE3TUMpHT4wTdPZbDbYOEmSLMsAIM9zIUSapoiYZVmapoSQPy8M/AQBRg0+AAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/24f54979aecd59d68bea0d70a531727d/8ae3e/ex_8_1.png",
                  "srcSet": ["/version/4.9.1/static/24f54979aecd59d68bea0d70a531727d/a2ead/ex_8_1.png 259w", "/version/4.9.1/static/24f54979aecd59d68bea0d70a531727d/6b9fd/ex_8_1.png 518w", "/version/4.9.1/static/24f54979aecd59d68bea0d70a531727d/8ae3e/ex_8_1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            When using multiple buttons, you can place a text button next to a
            solid button to show emphasis.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAABLElEQVQoz82Rz0rDQBCH8xxevHjxEbx68+oTdDfbzSVn8SBeRFAEX0I8eChFCgoefIEeWkQE/9SArRAhaZrGbJKdzY4kOZTaUognP34sw8C3MzAG1kFrjYij0ci2bc65kdZBCFHJlmURQoy8DgCglJrJtdauvvgPMqxGyiyTSZpViUUiQa2S5TwA8OtUw+Fw+dp5jlJqAATQErRSGAvVuf1oXTvtjnPVGjy/frvuJ+e8kPU8APk0ku9O+PI2GTghIj4+jdc3L7e22zu7N2sbF3sH3Wj6ZZrNJZO1RgBMUi0SFEnRCSby6KR/fNY/PX/YP+ze3bu+73JuNRoNw1/A88q3LDzPD4JxFAVhWGWcpnGv12eMFZPZAs3mXBhjpjkLY4xSSgihlBrkT9CSH2UcsZhVZTW/AAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/0880d9b758c9ce0b8228e9b49dd7e90b/8ae3e/ex_8_2.png",
                  "srcSet": ["/version/4.9.1/static/0880d9b758c9ce0b8228e9b49dd7e90b/a2ead/ex_8_2.png 259w", "/version/4.9.1/static/0880d9b758c9ce0b8228e9b49dd7e90b/6b9fd/ex_8_2.png 518w", "/version/4.9.1/static/0880d9b758c9ce0b8228e9b49dd7e90b/8ae3e/ex_8_2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            When using multiple buttons in the bottom bar of a modal, the
            buttons align to the right with the most important button on the far
            right.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "69.88416988416989%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAzUlEQVQoz82STQuCQBCG96e6i6kHf6xEhyCig1pgWmSkM/s1sSqFhz62Uw9zGBaeeYfdZeSDtZaIiqJI0zSKIgY+dF03ynEcc86Z8UEppbV+yl5rjyP+QVafkANjj4h+8sOXUgLA7La/eFtCaZWyUllEPcj5JNs5xrhv0F5xuWr2hxsRAeoe9HZ3WW/O1dGd5Hn+LtkSKU3GTMlGEyD1QIBudFmWSZIEQcCaF5wcU1/XU1VV3bbXLMvCMHTJ4RcsFrMSQnDOhRCM/4QYuAN4VsKHoU+i2AAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/369783ac0cea9e1165f44843412bd9a6/8ae3e/ex_8_3.png",
                  "srcSet": ["/version/4.9.1/static/369783ac0cea9e1165f44843412bd9a6/a2ead/ex_8_3.png 259w", "/version/4.9.1/static/369783ac0cea9e1165f44843412bd9a6/6b9fd/ex_8_3.png 518w", "/version/4.9.1/static/369783ac0cea9e1165f44843412bd9a6/8ae3e/ex_8_3.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            You can place a text button (low emphasis) next to an outlined
            button (medium emphasis).
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
  `}</div>
        </div></div>
      <hr></hr>
      <h2>{`Responsive Behavior`}</h2>
      <p>{`Button sizes and placements will sometimes need to change because of the size of the user’s screen. CSS breakpoints should be determined based on need, instead of limited to a set of standard breakpoints.`}</p>
      <div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "640px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "56.75675675675676%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAIAAADwazoUAAAACXBIWXMAAAsTAAALEwEAmpwYAAABkklEQVQoz5WQy07CQBSGeSHd6oI10WgCRgnYSgyz6lhfp/sKLQRYGkVuwVeQoIaLitANsabtzDChTHF0AF2ZiH++OZnL/89JTghCiBCilE6n/ppQShFCEMKQoiiEEM75fM44D/5kYeOEEEVRVmHHYUOLDF7x65AsGY4WWIvjSDAYrm4ch63Cqgpt25NAMxK9SsPb43RDAg053YinaonTevykJgFxkzytL2skeiWBpm17qgpD5+rZeOyGI5eb26V4qnYoVUWVq7sH1zux673DcixZiSWr0UQlnqrtH91sbJXCkcvx2D1Xz0KLgeFyxcqYnQuzk8l1jXzXLPTyxb5Z6IlNqZ8r9sxCP5PrZPPdjNkpVyyEsBgYhArGYmCcf4j1wRmbY8wwZtNp4PuB4/gemhHC+ErChjGBUBGdXdejNJjNBL7PXgZe+8G+f3zvPbsjC9+13lpt++nZ9X229FAauK637AwnE/rz5RoStsmEijAAQNM0w8jqun7xLV3/he8n3TCymqYBAERYlmXpn5Jl+Sv8CeHy5ogPcTMIAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/284680aba38499245fefa5c0e1c35755/6af66/ex_9_1.png",
                  "srcSet": ["/version/4.9.1/static/284680aba38499245fefa5c0e1c35755/a2ead/ex_9_1.png 259w", "/version/4.9.1/static/284680aba38499245fefa5c0e1c35755/6b9fd/ex_9_1.png 518w", "/version/4.9.1/static/284680aba38499245fefa5c0e1c35755/6af66/ex_9_1.png 640w"],
                  "sizes": "(max-width: 640px) 100vw, 640px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            You may allow the buttons to go block level if it benefits the
            design or if there isn’t enough room for them to remain
            side-by-side.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "640px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "56.75675675675676%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAIAAADwazoUAAAACXBIWXMAAAsTAAALEwEAmpwYAAABO0lEQVQoz52QMUvDQBiG81ucXVzF1T8g2OqBCCGVXCKKQwdxsV3qj3DpoIubi4qDeNC1palEWtoIIghWkLtcLneXJqdJ0FKwpfrwTi/fw/fdaRBCjLGUUs2NlBJjDCHUIISEkL/KhJCxzBiTMxFinCBghHxvppTGcSyEmBwVP40QgnPBs4ZzLuXI9/1UNk2TUqqUSpJknpvzMUqpaZq/yELEnI/CMGJhlJf1s+72zt1+ubFXbpR27yu1Jsa+lW8mhCiloigaZTy/kFZ76HSGzsM7Y+lH2gdoYfF8fetmDVwvLV+srF6+DT9sO3szY2za2Xl5dft6eNysnjiVWvuo2jqte0HALAtqhmEghFzXdRynM4Ve1/UGj4Ms/b775PUQQoZhaLquAwA2Z1IsbhQKEwEA6LqeyqV/8SV/AmxV/sk+awIKAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/4.9.1/static/6df9a7ded2be6ce89076d39e9c8388d7/6af66/ex_9_2.png",
                  "srcSet": ["/version/4.9.1/static/6df9a7ded2be6ce89076d39e9c8388d7/a2ead/ex_9_2.png 259w", "/version/4.9.1/static/6df9a7ded2be6ce89076d39e9c8388d7/6b9fd/ex_9_2.png 518w", "/version/4.9.1/static/6df9a7ded2be6ce89076d39e9c8388d7/6af66/ex_9_2.png 640w"],
                  "sizes": "(max-width: 640px) 100vw, 640px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            No need to force buttons to be block level if they fit in the
            container without problem.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
      </div>
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      